import Api from '@/config/api';
import mutations from '@/config/mutations';
import apiClient from '@/http';
import store from '@/store';
import router from '@/router';
import { generateCodeVerifier, generateCodeChallenge } from '@/utils/pkce';

const SESSION_KEY = 'oauth_session';
const PKCE_VERIFIER_KEY = 'pkce_code_verifier';
const OAUTH_STATE_KEY = 'oauth_state';

function generateState() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array)
    .map(function (b) {
      return b.toString(16).padStart(2, '0');
    })
    .join('');
}

function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem('showJdSection');
  store.commit(mutations.auth.SET_TOKEN, null);
  store.commit('clearAll');
  store.commit('clearTemplate');
  store.commit('jd/clearAll');
  store.commit('jd/clearAllJds');
}

/**
 * Maps backend 422 validation errors. Backend typo: use err.messsage (three s's).
 * @param {Array} errors - response.data.errors
 * @returns {Object} { fieldName: message }
 */
function mapValidationErrors(errors) {
  const fieldErrors = {};
  if (errors && errors.length) {
    for (let i = 0; i < errors.length; i++) {
      const err = errors[i];
      const messages = err.messsage;
      fieldErrors[err.field] =
        messages && messages.length ? messages[0] : '';
    }
  }
  return fieldErrors;
}

export default {
  authorize(username, password) {
    const codeVerifier = generateCodeVerifier();
    localStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
    const state = generateState();
    localStorage.setItem(OAUTH_STATE_KEY, state);

    return generateCodeChallenge(codeVerifier).then(
      function (codeChallenge) {
        const payload = {
          client_id: Api.credentials.CLIENT_ID,
          username: username,
          password: password,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
          state: state,
          isNewPanel: true,
        };

        return apiClient.post(Api.endpoints.OAUTH_AUTHORIZE, payload, {
          skipAuthRefresh: true,
        });
      }
    );
  },

  exchangeToken(authorizationCode) {
    const codeVerifier = localStorage.getItem(PKCE_VERIFIER_KEY);
    localStorage.removeItem(PKCE_VERIFIER_KEY);
    localStorage.removeItem(OAUTH_STATE_KEY);

    const payload = {
      client_id: Api.credentials.CLIENT_ID,
      authorization_code: authorizationCode,
      code_verifier: codeVerifier,
    };

    return apiClient.post(Api.endpoints.OAUTH_TOKEN, payload);
  },

  refreshToken() {
    return apiClient.post(Api.endpoints.OAUTH_REFRESH);
  },

  logout() {
    return apiClient.post(Api.endpoints.OAUTH_LOGOUT, {}, {
      skipAuthRefresh: true,
    }).then(
      function () {
        clearSession();
        router.push('/');
      },
      function () {
        clearSession();
        router.push('/');
      }
    );
  },

  setToken(payload) {
    if (!payload) {
      clearSession();
      return;
    }
    localStorage.setItem(SESSION_KEY, 'true');
  },

  getToken() {
    if (localStorage.getItem(SESSION_KEY) !== 'true') {
      return null;
    }
    return { session: true };
  },

  isLoggedIn() {
    return localStorage.getItem(SESSION_KEY) === 'true';
  },

  mapValidationErrors: mapValidationErrors,
};
